import { useCallback, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import { classNames } from "@ct-react/core";
import { ArticleType } from "@shared/url";
import { AnyArticleDetails } from "@shared/graphql";
import { articleTypes } from "../i18n/sharable-defs";
import { AnyArticleSearch } from "../contexts/search";
import { useBookmarksContext } from "../contexts/bookmarks";
import { formatWhere } from "../hooks/format-gql-variables";
import SeoMetas from "../components/layout/seo-metas";
import PageHero from "../components/layout/page-elements/page-hero";
import { Wrapper } from "../components/layout/wrapper";
import ArticleCard from "../components/article-card";
import bgPath from "../assets/bg-images/hero-bookmarks.webp";
import "./bookmarks.scss";

const BOOKMARKS_GQL_DATA = gql`
  query Bookmarks($where: AnyArticleFilterInput, $sort: AnyArticleSortInput, $first: Int, $last: Int, $before: String, $after: String) {
    items: search(where: $where, sort: $sort, first: $first, last: $last, before: $before, after: $after) {
      totalCount
      edges {
        cursor
        node {
          ... on RentalAccommodationArticle {
            id slug
            novelty recommended promoted
            images @limit(max: 10) { assets { url breakpoint } }
            address { city }
            title { value }
            lowestPrice { type onDemand value }
            features { maxGuests bedrooms bathrooms }
          }
          ... on SaleAccommodationArticle {
            id slug
            novelty recommended promoted sold
            images @limit(max: 10) { assets { url breakpoint } }
            address { city }
            title { value }
            price { onDemand value }
            features { rooms bedrooms bathrooms livingSpace plotSpace }
          }
        }
        accommodationBookingSuggestion {
          period bookable priceOnDemand
          price { amount detailed { bare discount providerCharge { amount adaptive } } }
        }
      }
      paginationInfo {
        currentPage pageCount pageSize hasNextPage hasPreviousPage
        lastPageCursor nextPageCursor previousPageCursor linkPageCursors { cursor page }
      }
    }
  }
`;

const transDefs = defineMessages({
  title: { id: "bookmarks.title", defaultMessage: "Vos favoris" },
  lead: { id: "bookmarks.lead", defaultMessage: "Retrouvez ici les biens que vous avez sélectionnés." },
  empty: { id: "bookmarks.empty", defaultMessage: "Vous n'avez enregistré aucun article de ce type dans vos favoris." }
});

const BookmarksPage = () => {

  const intl = useIntl();
  const client = useApolloClient();
  const { saves: bookmarks } = useBookmarksContext();

  const [ loading, setLoading ] = useState<boolean>(true);
  const [ content, setContent ] = useState<Record<ArticleType, AnyArticleDetails[]>>();
  const [ propertiesDisplay, setPropertiesDisplay ] = useState<ArticleType>(ArticleType.SALE);

  useEffect(() => {
    setLoading(true);
    Promise.all(
      Object.entries(bookmarks).map(async ([ articleType, ids ]) => {
        if (ids.length === 0) return new Promise(resolve => resolve({ articleType, result: [] }));
        return client.query<{ items: any}>({
          query: BOOKMARKS_GQL_DATA,
          variables: { first: 50, where: formatWhere({kind: articleType, ids } as AnyArticleSearch)}
        }).then(({ data }) => ({ articleType, result: data!.items.edges.map((edge: any) => edge.node) }))
      })
    ).then((result: any[]) => {
      const casted = result.reduce((acc, cur) => ({ ...acc, [cur.articleType]: cur.result }), {} as Record<ArticleType, AnyArticleDetails[]>);
      setContent(casted);
    }).finally(() => setLoading(false));
  }, [ bookmarks ]);

  const display = useCallback((type: ArticleType) => {
    if (content![type].length === 0)
      return (<p className="empty">{intl.formatMessage(transDefs.empty)}</p>)
    return content![type].map(item => (<ArticleCard key={item.id} loading={false} data={item} />));
  }, [ content, intl ]);

  return (
    <>

      <SeoMetas title={`${intl.formatMessage(transDefs.title)} | EV Real Estate`} />
      <meta name="robots" content="noindex" />

      <PageHero title={intl.formatMessage(transDefs.title)}
                chapo={intl.formatMessage(transDefs.lead)}
                background={bgPath} />

      <Wrapper.Centered className="bookmarks content">

        <div className="p-selector">
          <button type="button"
                  className={classNames("btn","outline", "cream", {selected: propertiesDisplay === ArticleType.SALE})}
                  onClick={() => setPropertiesDisplay(ArticleType.SALE)}>
            <span className="txt">{intl.formatMessage(articleTypes.nav[ArticleType.SALE])}</span>
            {(!!content && content[ArticleType.SALE].length > 0) &&
              <span className="badge">{content[ArticleType.SALE].length}</span>
            }
          </button>
          <button type="button"
                  className={classNames("btn","outline", "cream", {selected: propertiesDisplay === ArticleType.RENTAL})}
                  onClick={() => setPropertiesDisplay(ArticleType.RENTAL)}>
            <span className="txt">{intl.formatMessage(articleTypes.nav[ArticleType.RENTAL])}</span>
            {(!!content && content[ArticleType.RENTAL].length > 0) &&
              <span className="badge">{content[ArticleType.RENTAL].length}</span>
            }
          </button>
        </div>

        <main>
          {(loading || !content)
            ? Array.from({ length: 4 }).map((_, index) => (<ArticleCard key={index} loading={true} />))
            : display(propertiesDisplay)
          }
        </main>

      </Wrapper.Centered>

    </>);

};

export default BookmarksPage;
